const heroes = new WeakMap()

function isMobileViewport() {
  return window.matchMedia('(max-width: 768px)').matches
}

function getActiveVideos(slide) {
  if (!slide) return []

  const hasSplit = slide.querySelector('.coll-hero__video--desktop')
  if (!hasSplit) {
    return [...slide.querySelectorAll('video')]
  }

  const selector = isMobileViewport()
    ? '.coll-hero__video--mobile video'
    : '.coll-hero__video--desktop video'

  return [...slide.querySelectorAll(selector)]
}

function syncVideos(root, activeIndex) {
  const slides = [...root.querySelectorAll('[data-coll-hero-slide]')]

  slides.forEach((slide, index) => {
    const videos = slide.querySelectorAll('video')
    const shouldPlay = index === activeIndex
    const activeVideos = shouldPlay ? getActiveVideos(slide) : []

    videos.forEach((video) => {
      video.muted = true
      video.playsInline = true
      video.loop = true

      if (activeVideos.includes(video)) {
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {})
        }
      } else {
        video.pause()
      }
    })
  })
}

function setupReadMore(root) {
  const desc = root.querySelector('[data-coll-hero-desc]')
  const inner = root.querySelector('[data-coll-hero-desc-inner]')
  const toggle = root.querySelector('[data-coll-hero-toggle]')
  const label = root.querySelector('[data-coll-hero-toggle-label]')

  if (!desc || !inner || !toggle) return

  const lines = Math.max(1, parseInt(desc.dataset.descLines || root.style.getPropertyValue('--coll-hero-desc-lines') || '2', 10))
  desc.style.setProperty('--coll-hero-desc-lines', String(lines))

  const setLabel = (expanded) => {
    const text = expanded
      ? toggle.dataset.less || 'Læs mindre'
      : toggle.dataset.more || 'Læs mere'
    if (label) label.textContent = text
    else toggle.textContent = text
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
  }

  const checkOverflow = () => {
    const wasExpanded = toggle.getAttribute('aria-expanded') === 'true'

    desc.classList.remove('is-collapsed', 'is-expanded')
    toggle.hidden = true

    const styles = window.getComputedStyle(inner)
    const lineHeight = parseFloat(styles.lineHeight) || 24
    const fullHeight = inner.scrollHeight
    const maxCollapsedHeight = lineHeight * lines
    const overflows = fullHeight > maxCollapsedHeight + 2

    if (!overflows) {
      setLabel(false)
      return
    }

    toggle.hidden = false

    if (wasExpanded) {
      desc.classList.add('is-expanded')
      setLabel(true)
    } else {
      desc.classList.add('is-collapsed')
      setLabel(false)
    }
  }

  toggle.addEventListener('click', () => {
    const expanded = desc.classList.contains('is-expanded')
    desc.classList.toggle('is-collapsed', expanded)
    desc.classList.toggle('is-expanded', !expanded)
    setLabel(!expanded)
  })

  checkOverflow()
  window.addEventListener('resize', checkOverflow, { passive: true })
}

function createDots(root, count, goTo) {
  const dotsWrap = root.querySelector('[data-coll-hero-dots]')
  if (!dotsWrap || count < 2) return []

  dotsWrap.innerHTML = ''
  const dots = []

  for (let i = 0; i < count; i += 1) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'coll-hero__dot'
    button.setAttribute('aria-label', `Go to slide ${i + 1}`)
    button.addEventListener('click', () => goTo(i))
    dotsWrap.appendChild(button)
    dots.push(button)
  }

  return dots
}

function initHero(root) {
  if (!root || heroes.has(root)) return

  setupReadMore(root)

  const track = root.querySelector('[data-coll-hero-track]')
  const slides = [...root.querySelectorAll('[data-coll-hero-slide]')]
  const prev = root.querySelector('[data-coll-hero-prev]')
  const next = root.querySelector('[data-coll-hero-next]')
  const pauseBtn = root.querySelector('[data-coll-hero-pause]')
  const pauseIcon = root.querySelector('[data-pause-icon]')
  const playIcon = root.querySelector('[data-play-icon]')

  if (!track || slides.length === 0) {
    heroes.set(root, { destroy() {} })
    return
  }

  let index = 0
  let timer = null
  let paused = false
  const autoplay = root.dataset.autoplay === 'true' && slides.length > 1
  const speed = Math.max(2, parseInt(root.dataset.autoplaySpeed || '5', 10)) * 1000

  const dots = createDots(root, slides.length, (i) => goTo(i, true))

  function updateUI() {
    track.style.transform = `translateX(-${index * 100}%)`
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index)
    })
    syncVideos(root, index)
  }

  function stopTimer() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function startTimer() {
    stopTimer()
    if (!autoplay || paused) return
    timer = window.setInterval(() => {
      goTo(index + 1)
    }, speed)
  }

  function goTo(nextIndex, userAction = false) {
    index = ((nextIndex % slides.length) + slides.length) % slides.length
    updateUI()
    if (userAction) startTimer()
  }

  function setPaused(nextPaused) {
    paused = nextPaused
    if (pauseIcon) pauseIcon.hidden = paused
    if (playIcon) playIcon.hidden = !paused
    if (pauseBtn) {
      pauseBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow')
    }
    if (paused) stopTimer()
    else startTimer()
  }

  prev?.addEventListener('click', () => goTo(index - 1, true))
  next?.addEventListener('click', () => goTo(index + 1, true))
  pauseBtn?.addEventListener('click', () => setPaused(!paused))

  let touchStartX = 0
  track.addEventListener(
    'touchstart',
    (event) => {
      touchStartX = event.changedTouches[0].screenX
    },
    { passive: true }
  )
  track.addEventListener(
    'touchend',
    (event) => {
      const delta = event.changedTouches[0].screenX - touchStartX
      if (Math.abs(delta) < 40) return
      goTo(delta < 0 ? index + 1 : index - 1, true)
    },
    { passive: true }
  )

  const onResize = () => syncVideos(root, index)
  window.addEventListener('resize', onResize, { passive: true })

  updateUI()
  startTimer()

  heroes.set(root, {
    destroy() {
      stopTimer()
      window.removeEventListener('resize', onResize)
    }
  })
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-coll-hero]').forEach((root) => initHero(root))
}

initAll()

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target)
})

document.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest('[data-coll-hero]')
  if (root) initHero(root)
})
